import Promise from 'bluebird';
import _ from 'lodash';

import { CONFIG } from '../../config';
import { ContextAttributesEnum } from '../../enuns/contextAttributes.enum';
import { StoreTypeEnum } from '../../enuns/store.enun';
import { normalizeStoreTypeSaleMarketValue, treatStoreTypeSaleMarketValue } from '../../models/StoreTypeSaleMarket';
import api from '../../scripts/api';
import store from '../../store';
import { OVERVIEW_PANEL_GET_CURRENT, OVERVIEW_PANEL_IS_VISIBLE } from '../../store/modules/overview-panel/types';
import worldMap from '../core/map';
import { pinBuilder } from '../core/map/pinBuilder';
import streetView from '../core/map/streetview';
import templates from '../core/templates';
import ColorBrewer from '../core/tools/colorbrewer';
import translate from '../core/tools/translate';
import { VehicleTypeEnum } from '../core/vehicleType/constants';
import environment from '../environment';
import HaloLayer from './HaloLayer';
import Leaflet from './Leaflet';
import { FeaturePermission } from '../../constants/feature.permission.helper';

// import 'mapbox.js';
const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);
const LayerControls = name => require('../controls').get(name);
const get = (name, currentMap = worldMap.getCurrent()) => {
    return require('./index').default.get(name, currentMap);
};

class StoresLayer {
    constructor(names, contextType, currentMap) {
        let context;
        let _total = 0;
        const mapId = +currentMap._id;

        const clusterImage = new Image();

        switch (contextType) {
            case 'Dealerships': {
                context = 'stores';
                clusterImage.src = `${environment.get('IMAGES_HOST')}/icon_marker_my_stores.png`;
                break;
            }
            case 'StoresMotocycle': {
                context = 'storesMotocycle';
                clusterImage.src = `${environment.get('IMAGES_HOST')}/icon_marker_my_stores.png`;
                break;
            }
            case 'StoresConsortium': {
                context = 'storesConsortium';
                clusterImage.src = `${environment.get('IMAGES_HOST')}/icon_marker_my_stores.png`;
                break;
            }
            case 'Competitors': {
                context = 'competitors';
                clusterImage.src = `${environment.get('IMAGES_HOST')}/icon_marker_stores.png`;
                break;
            }
            case 'StoreInactive': {
                context = 'storeInactive';
                clusterImage.src = `${environment.get('IMAGES_HOST')}/icon_marker_stores.png`;
                break;
            }
            case 'UnapprovedStores': {
                context = 'unapprovedStores';
                clusterImage.src = `${environment.get('IMAGES_HOST')}/icon_marker_stores.png`;
                break;
            }
            case 'StorePlanned': {
                context = 'storePlanned';
                clusterImage.src = `${environment.get('IMAGES_HOST')}/icon_marker_stores.png`;
                break;
            }
        }

        if (!_.isObject(names)) {
            names = [names];
        }

        const onComplete = features => {
            try {
                if (LayerControls('Voronoi').getControls(mapId).switch.onOff.status) {
                    LayerControls('Voronoi').update(mapId);
                }
            } catch (error) {
                console.log('Voronoi Layer not available');
            }

            try {
                if (
                    LayerControls('ServiceArea').getControls(mapId).switch.onOff.status &&
                    LayerControls('ServiceArea').getControls(mapId).checkbox.loadFromAllVisibleStores.status
                ) {
                    LayerControls('ServiceArea').toggleAll(currentMap);
                }
            } catch (error) {
                console.log('Service Area Layer not available');
            }

            LayerControls(contextType).show({ refresh: true }, features);

            if (contextType === 'Dealerships' || contextType === 'StoresConsortium') {
                try {
                    LayerControls('Brownfield').stack(currentMap);
                } catch (error) {
                    console.log('Brownfield Layer not available');
                }

                try {
                    LayerControls('NewGreenfield').stack(currentMap);
                } catch (error) {
                    console.log('Greenfield Layer not available');
                }
            }

            if (
                store.getters[OVERVIEW_PANEL_GET_CURRENT] === contextType &&
                store.getters[OVERVIEW_PANEL_IS_VISIBLE] &&
                LayerControls(contextType).getControls(mapId).button.overview.enable
            ) {
                LayerControls(contextType).toggleOverview();
            }

            _total = 0;
        };

        const onRemove = () => {
            try {
                if (LayerControls('Voronoi').getControls(mapId).switch.onOff.status) {
                    LayerControls('Voronoi').update(mapId);
                }
            } catch (error) {
                console.log('Layer not avaliable');
            }

            if (contextType === 'Dealerships' || contextType === 'StoresConsortium') {
                try {
                    LayerControls('Brownfield').stack(currentMap);
                } catch (error) {
                    console.log('Brownfield Layer not available');
                }

                try {
                    LayerControls('NewGreenfield').stack(currentMap);
                } catch (error) {
                    console.log('Greenfield Layer not available');
                }
            }
        };

        const onSuccess = values => {
            return {
                ...values,
                features: values.features.map(f => ({
                    ...f,
                    properties: {
                        ...f.properties,
                        Tipo: translate(f.properties.Tipo),
                        TipoVendaMercado: f.properties.TipoVendaMercado
                            ? normalizeStoreTypeSaleMarketValue(f.properties.TipoVendaMercado)
                            : f.properties.TipoVendaMercado
                    }
                }))
            };
        };

        const onReload = async features => {
            const f = LayerControls(contextType);
            const layerType = f.getControls(mapId).buttonGroup.layerType.type;

            if (layerType === 'decluster') {
                get(contextType, currentMap).setOptions({ controlRange: 0.025, type: 'cluster' });
            } else if (layerType === 'cluster' && get(contextType, currentMap).options.controlRange === 1) {
                get(contextType, currentMap).setOptions({ controlRange: 30 });
            }

            return await onSuccess(features);
        };

        const onClick = (feature, layer) => {
            LayerControls(contextType).highlight(layer);
            LayerControls(contextType).show({
                show: true,
                id: feature.properties.Id
            });

            layer.closePopup();
            return new HaloLayer(currentMap, layer, feature, contextType, context);
        };

        const createSmallestPinClusterIcon = control =>
            L.icon({
                iconUrl: `${environment.get('IMAGES_HOST')}/${control.smallestPinIcon}`,
                iconSize: [14, 14],
                iconAnchor: [14, 14]
            });

        const createClusterTemplate = cluster => {
            const f = LayerControls(contextType);
            const showSales = f.getControls(mapId).checkbox.tagValue;
            const layerType = f.getControls(mapId).buttonGroup.layerType.type;

            if (layerType === 'decluster' && f.isSmallestPinSelected(mapId)) {
                return createSmallestPinClusterIcon(f);
            }

            const images = _(cluster.getAllChildMarkers())
                .map(m =>
                    environment.get('CONFIG.USERVEHICLETYPE') === VehicleTypeEnum.Moto || context === 'storesMotocycle'
                        ? `${environment.get('IMAGES_HOST')}/motos/pin_${String(m.feature.properties.Bandeira).toUnderscoreCase()}_xs.png`
                        : `${environment.get('IMAGES_HOST')}/pin_${String(m.feature.properties.Bandeira).toUnderscoreCase()}_xs.png`)
                .uniqBy(i => i)
                .value();

            let data;
            if ((showSales || {}).status) {
                var features = cluster.getAllChildMarkers().filter(f => f.feature.properties.IdTipoLoja !== StoreTypeEnum.Oficina);

                if (!_total) {
                    _total = _.sumBy(get(contextType, currentMap).getCurrentFeatures(), 'properties.MediaMensal') || 0;
                }

                data = {
                    total: Math.round(_total),
                    value: Math.round(_.sumBy(features, 'feature.properties.MediaMensal')) || 0,
                    images
                };

                if (data.value === 0) {
                    return L.icon({
                        iconUrl: `${environment.get('IMAGES_HOST')}/icon_blank.png`,
                        iconSize: L.point(0, 0),
                        iconAnchor: [0, 0]
                    });
                }
            } else {
                data = {
                    value: cluster.getChildCount(),
                    images
                };
            }

            if (currentMap.getZoom() < 14 && layerType !== 'decluster') {
                return pinBuilder.create.pin.imageWithValue(data, clusterImage);
            } else {
                return pinBuilder.create.pin.cluster(data);
            }
        };

        const createPopupTemplate = (properties, layer) =>
            new Promise((resolve, reject) => {
                const rfeatures = get('Retention', currentMap).getFeatures();
                const store = LayerControls('Retention').getStore();
                let chart;

                if (
                    (store ? store.feature.properties.Id : 0) === properties.Id &&
                    LayerControls('Retention').check() &&
                    _.size(rfeatures)
                ) {
                    properties.Retention = LayerControls('Retention').calculate(rfeatures);
                    if (_.size(properties.Retention.aggregate) % 2 !== 0) {
                        properties.Retention.aggregate.push([]);
                    }
                    chart = true;
                } else {
                    delete properties.Retention;
                }

                if (context === 'storesMotocycle') {
                    delete properties.TipoVendaMercado;
                }

                if (properties.TipoVendaMercado &&
                    ((contextType === 'Dealerships' && isAuthorized(FeaturePermission.LAYER_NETWORK.filterStoreTypeSaleMarket))
                     || (contextType === 'Competitors' && isAuthorized(FeaturePermission.LAYER_COMPETITORS.filterStoreTypeSaleMarket)))) {
                    properties.storeTypeSaleMarket = treatStoreTypeSaleMarketValue(properties.TipoVendaMercado);
                }

                if (contextType === 'Competitors') {
                    delete properties.IdLojaCliente;
                }

                if (!isAuthorized(FeaturePermission.LAYER_NETWORK.showGroupClient) && !properties.GrupoCliente) {
                    delete properties.GrupoCliente;
                }

                let $template = $(templates('map-layer-stores-popup', properties));

                if (contextType === 'UnapprovedStores') {
                    $template = $(templates('map-layer-unapproved-stores-popup', properties));
                }

                const imageUrl = `${CONFIG.STORE_THUMBS_HOST}/${properties.IdOriginal}.jpg`;

                const coordinates = {
                    lat: layer.feature.geometry.coordinates[1],
                    lng: layer.feature.geometry.coordinates[0]
                };

                streetView.thumbnail($template.find('.leaflet-popup-image'), coordinates, imageUrl).then(() => {
                    if (chart) {
                        setTimeout(() => {
                            $('.leaflet-popup-retention')
                                .find('.col-xs-12')
                                .kendoChart({
                                    title: {
                                        text: ''
                                    },
                                    dataSource: {
                                        data: _.filter(properties.Retention.aggregate, d => d.value > 0)
                                    },
                                    seriesColors: _.map(properties.Retention.aggregate, d => d.color),
                                    seriesDefaults: {
                                        labels: {
                                            position: 'center',
                                            visible: true,
                                            background: 'transparent',
                                            format: '{0}',
                                            font: `bold 10px 'Roboto Condensed', sans-serif`,
                                            color: '#FFF'
                                        }
                                    },
                                    theme: 'bootstrap',
                                    chartArea: {
                                        width: 260,
                                        height: 140,
                                        margin: {
                                            top: 0,
                                            bottom: 0,
                                            left: 10,
                                            right: 10
                                        }
                                    },
                                    legend: {
                                        position: 'right',
                                        labels: {
                                            font: "10px 'Roboto Condensed', sans-serif"
                                        }
                                    },
                                    series: [
                                        {
                                            type: 'pie',
                                            field: 'value',
                                            categoryField: 'text'
                                        }
                                    ]
                                });
                        }, 300);
                    }
                    resolve($template);
                });
            });

        const createClusterPopupTemplate = features => {
            if (worldMap.getZoom() < 8) return;

            const featuresFilted = features.filter(f => f.feature.properties.IdTipoLoja !== StoreTypeEnum.Oficina);

            if (!featuresFilted.length) {
                return;
            }

            const _isSalesEnabled = (LayerControls(contextType).getControls(mapId).checkbox.tagValue || {}).status;
            const _totalSales = _isSalesEnabled ? _.sumBy(featuresFilted, 'feature.properties.TotalVendas') : 0;
            const _totalStores = _.size(featuresFilted);
            const brands = _(featuresFilted)
                .groupBy('feature.properties.Bandeira')
                .map((v, k) => ({
                    icon: environment.get('CONFIG.USERVEHICLETYPE') === VehicleTypeEnum.Moto || context === 'storesMotocycle'
                        ? `${environment.get('IMAGES_HOST')}/motos/logo_${String(k).toUnderscoreCase()}.png`
                        : `${environment.get('IMAGES_HOST')}/logo_${String(k).toUnderscoreCase()}.png`,
                    sales: _isSalesEnabled ? _.sumBy(v, 'feature.properties.MediaMensal') : 0,
                    salesPerBrand: _isSalesEnabled ? _.sumBy(v, 'feature.properties.MediaMensal') / _.size(v) : 0,
                    total: _.size(v),
                    name: k.toCapitalCase()
                }))
                .orderBy(_isSalesEnabled ? 'sales' : 'total')
                .reverse()
                .value();

            const values = {
                brands,
                period: LayerControls(contextType).currentPeriod,
                totalBrands: _.size(brands),
                totalStores: _totalStores,
                totalAvgSalesInCluster: _.sumBy(brands, 'sales'),
                totalAvgSales: _.sumBy(brands, 'sales') / _totalStores,
                totalSales: _totalSales,
                salesEfficiency: _totalSales
            };
            return $(templates('map-layer-stores-cluster-popup', values));
        };

        let urlApi = '';
        switch (contextType) {
            case 'Dealerships':
                urlApi = `${environment.get('API_HOST')}/lojaconcessionaria/GetStoreDealersFiltered`;
                break;
            case 'StoresMotocycle':
                urlApi = `${environment.get('API_HOST')}/lojaconcessionaria/GetStoresMotocycle`;
                break;
            case 'StoresConsortium':
                urlApi = `${environment.get('API_HOST')}/lojaconcessionaria/GetStoresConsortium`;
                break;
            case 'Competitors':
                urlApi = `${environment.get('API_HOST')}/lojaconcessionaria/GetStoreCompetitors`;
                break;
            case 'StoreInactive':
                urlApi = `${environment.get('API_HOST')}/lojaconcessionaria/GetStoresInactive`;
                break;
            case 'UnapprovedStores':
                urlApi = `${environment.get('API_HOST')}/lojaconcessionaria/GetStoresUnnapproved`;
                break;
            case 'StorePlanned':
                urlApi = `${environment.get('API_HOST')}/lojaconcessionaria/GetPlannedStores`;
                break;
        }

        return new Leaflet.GeoJSONLayer({
            map: currentMap,
            id: `spatial_${contextType.toLowerCase()}_layer`,
            remote: {
                url: urlApi,
                method: 'POST',
                params: {
                    Bandeira: names.join(','),
                    TemplateResultType: contextType
                }
            },
            scaleRange: [5, 20],
            filter: {
                location: true
            },
            showAll: true,
            // expandView: 1,
            autofocus: false,
            uniqueField: 'Id',
            cache: true,
            // dynamic: true
            // controlRange: 10
            sort: 'Nome',
            click: onClick,
            done: onComplete,
            remove: onRemove,
            success: onSuccess,
            reload: onReload,
            // mouseover: (feature, event) ->
            //   get(contextType)._showPopup feature, event
            // mouseout: (feature, layer) ->
            //   get(contextType).closePopup()
            popup: {
                template: createPopupTemplate,
                clusterTemplate: createClusterPopupTemplate,
                trigger: 'mouseover',
                options: {
                    offset: L.point(0, -50),
                    closeButton: false,
                    autoclose: true
                },
                single: true
            },
            type: 'cluster',
            symbology: {
                type: 'polymorph',
                cluster: {
                    disableAtZoom: 20,
                    template: createClusterTemplate
                },
                heat: {
                    theme: ColorBrewer.getHeatColors(ColorBrewer.RED_YELLOW_GREEN)
                },
                style: {
                    icon: {
                        iconUrl: 'asdfasdfasdf',
                        getUrl: StoresLayer.getApplicatorUrlIcon(contextType),
                        iconSize: StoresLayer.getIconSize(contextType),
                        iconAnchor: [25, 60], // point of the icon which will correspond to marker's location
                        labelAnchor: [25, -32],
                        className: 'store-marker-icon',
                    },
                    riseOnHover: true
                }
            }
        });
    }

    static getIconSize = contextType => {
        return (data) => {
            const { IsActive } = data;

            if (contextType === 'UnapprovedStores' || contextType === 'StorePlanned') {
                return [50, 64];
            }

            if (!IsActive)
                return [38, 48];

            return [50, 64];
        };
    }

    static getApplicatorUrlIcon = contextType => {
        const storeContext = environment.get('CONFIG.FILTERS.STORE_CONTEXT_ATTRIBUTES');
        return (data) => {
            const filters = LayerControls(contextType).getLayerFilters();
            const { Bandeira, Tipo, TipoOriginal, IsPinClient, IsActive, IdAttributes, IsHmb } = data;
            const typeStore = String(Tipo).toUnderscoreCase();
            const typeStoreOriginal = String(TipoOriginal).toUnderscoreCase();
            const brand = String(Bandeira).toUnderscoreCase();
            const pin = IsPinClient ? typeStore : typeStoreOriginal;
            const has_filter_attributes = filters.properties
                && filters.properties.IdAttributes
                && filters.properties.IdAttributes.data.length;

            const filterEletricContext = x => IdAttributes.indexOf(x.idAttributeType) >= 0 && x.idAttributeContext === ContextAttributesEnum.Eletric;

            const url = environment.get('CONFIG.USERVEHICLETYPE') === VehicleTypeEnum.Moto || contextType === 'StoresMotocycle'
                ? `${environment.get('IMAGES_HOST')}/motos`
                : `${environment.get('IMAGES_HOST')}`;

            if (has_filter_attributes && storeContext.some(filterEletricContext) && brand === 'gm') {
                return `${url}/pin_${brand}_concessionaria_ev.png`;
            }

            if (contextType === 'UnapprovedStores' && environment.get('CONFIG.USERVEHICLETYPE') === VehicleTypeEnum.Moto) {
                return `${url}/pin_atention.png`;
            }

            if (contextType === 'StorePlanned') {
                return `${url}/pin_lojas_previstas_${pin}.png`;
            }

            if (contextType === 'StoresConsortium') {
                return `${url}/pin_${typeStore}_${typeStoreOriginal}.png`;
            }

            if (!IsActive) {
                return `${url}/pin_${brand}_inativa.png`;
            }

            if (IsHmb) {
                return `${url}/pin_hmb_${pin}.png`;
            }

            return `${url}/pin_${brand}_${pin}.png`;
        };
    }

    static getApplicatorUrlIconWithoutTypeStore = contextType =>
        ({ Bandeira, Tipo, TipoOriginal, IsPinClient }) => {
            const typeStore = `_${String(Tipo).toUnderscoreCase()}`;
            const pin = IsPinClient ? typeStore : "";
            const url = environment.get('CONFIG.USERVEHICLETYPE') === VehicleTypeEnum.Moto || contextType === 'StoresMotocycle'
                ? `${environment.get('IMAGES_HOST')}/motos`
                : `${environment.get('IMAGES_HOST')}`;
            const brand = String(Bandeira).toUnderscoreCase();

            if (contextType === 'UnapprovedStores' && environment.get('CONFIG.USERVEHICLETYPE') === VehicleTypeEnum.Moto) {
                return `${url}/pin_atention.png`;
            }

            if (contextType === 'StorePlanned') {
                return `${url}/pin_lojas_previstas.png`;
            }

            if (contextType === 'StoresConsortium') {
                return `${url}/pin_${String(Tipo).toUnderscoreCase()}_${String(TipoOriginal).toUnderscoreCase()}.png`;
            }

            return `${url}/pin_${brand}${pin}.png`;
        }
}

export default StoresLayer;



// WEBPACK FOOTER //
// ./src/scripts/layers/StoresLayer.js